import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import ApiHubBackend from "services/ApiHubBackend";
import obterUserEmail from "utils/userEmail";

// PO1-2053 (revisado) — modal de Cotação Spot Auto-Cadastro.
//
// Aparece quando a simulação volta vazia. Substituiu o fluxo antigo de
// email manual (com to/subject/body editáveis) — agora é apenas um
// confirm + dispatch automático para todas as transportadoras do contrato
// vigente, cada uma recebendo um link único para preencher o valor.
//
// Cores via tokens --everlog-* (vivem em :root e body.white-content), o
// que mantém o modal renderizado em portal respeitando tema claro/escuro.
//
// obterUserEmail foi extraído para utils/userEmail (PO1-2082) e é
// compartilhado com o EditCotacaoModal.

const SolicitarCotacaoModal = ({
  open,
  onClose,
  idEmbarcador,
  idCidadeDestino,
  rotaLabel, // string "Cidade/UF" — só pra display
  onSent,
  onError,
}) => {
  const [sending, setSending] = useState(false);
  const [resultado, setResultado] = useState(null);

  const handleConfirmar = async () => {
    setSending(true);
    try {
      const userEmail = obterUserEmail();
      // ApiHubBackend.baseURL já termina em "/api", então passar caminho
      // relativo sem prefixo (evita /api/api/...).
      const response = await ApiHubBackend.post("/cotacao-spot/disparar", {
        idEmbarcador,
        idCidadeDestino,
        userEmail,
      });
      setResultado(response.data);
      if (onSent) onSent(response.data);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        (err.response && err.response.data && err.response.data.error) ||
        "Falha ao solicitar cotação. Tente novamente.";
      if (onError) onError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResultado(null);
    setSending(false);
    if (onClose) onClose();
  };

  const transportadoras = resultado?.transportadorasNotificadas || [];

  // Cores via tokens — funcionam em portal (Modal) porque tokens vivem em :root
  // e body.white-content, não em .main-panel.
  const headerStyle = {
    padding: "16px 20px",
    borderBottom: "1px solid var(--everlog-card-divider, #e9ecef)",
    background: "var(--everlog-card-surface, #1e2640)",
    color: "var(--everlog-card-title, #f5f7fb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const closeBtnStyle = {
    background: "transparent",
    border: "none",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    color: "var(--everlog-card-muted, #8ea7d7)",
    padding: 0,
  };

  const bodyStyle = {
    background: "var(--everlog-card-surface, #1e2640)",
    color: "var(--everlog-card-text, #dce4ff)",
  };

  const footerStyle = {
    background: "var(--everlog-card-surface, #1e2640)",
    borderTop: "1px solid var(--everlog-card-divider, #e9ecef)",
  };

  const mutedTextStyle = {
    color: "var(--everlog-card-text-soft, rgba(255,255,255,0.6))",
    fontSize: 14,
  };

  const titleTextStyle = {
    color: "var(--everlog-card-title, #f5f7fb)",
    margin: 0,
    fontWeight: 600,
  };

  const warningTextStyle = {
    color: "var(--everlog-status-warning, #ff8d72)",
    marginTop: 0,
  };

  return (
    <Modal isOpen={!!open} toggle={handleClose} size="md" backdrop="static">
      <div style={headerStyle}>
        <strong style={titleTextStyle}>Solicitar atualização de tabela</strong>
        <button
          type="button"
          onClick={handleClose}
          style={closeBtnStyle}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody style={bodyStyle}>
        {!resultado && (
          <>
            <p style={{ marginTop: 0, color: "var(--everlog-card-text)" }}>
              A rota{rotaLabel ? ` ${rotaLabel}` : ""} não está cadastrada nas
              tabelas vigentes. Vamos enviar um link para{" "}
              <strong>todas as transportadoras do contrato</strong> preencherem
              o valor por veículo.
            </p>
            <p style={mutedTextStyle}>
              Cada transportadora recebe um link único e responde no seu tempo.
              Quando alguma responder, a tabela é atualizada automaticamente e
              você é avisado por email para refazer a simulação.
            </p>
          </>
        )}

        {resultado && resultado.disparado && transportadoras.length > 0 && (
          <>
            <p style={{ marginTop: 0, color: "var(--everlog-card-text)" }}>
              <strong>{transportadoras.length}</strong>{" "}
              {transportadoras.length === 1
                ? "transportadora foi notificada"
                : "transportadoras foram notificadas"}
              :
            </p>
            <ul
              style={{
                marginBottom: 12,
                color: "var(--everlog-card-text)",
                paddingLeft: 20,
              }}
            >
              {transportadoras.map((t) => (
                <li key={t.idTransportadora}>
                  <strong>{t.nomeTransportadora}</strong>{" "}
                  <span style={mutedTextStyle}>
                    ({t.qtdVeiculos}{" "}
                    {t.qtdVeiculos === 1 ? "veículo" : "veículos"})
                  </span>
                </li>
              ))}
            </ul>
            <p style={mutedTextStyle}>
              Você receberá um email assim que alguma transportadora responder.
            </p>
          </>
        )}

        {resultado &&
          !resultado.disparado &&
          (resultado.transportadorasSemEmail || 0) > 0 && (
            <p style={warningTextStyle}>
              Não foi possível notificar as transportadoras porque elas não têm
              contato do tipo <strong>Cotação</strong> cadastrado. Acione o
              comercial pra cadastrar os emails antes de seguir.
            </p>
          )}

        {resultado &&
          !resultado.disparado &&
          (resultado.transportadorasSemEmail || 0) === 0 && (
            <p style={mutedTextStyle}>
              Essa rota já está coberta por todas as transportadoras do
              contrato. Tente refazer a simulação.
            </p>
          )}
      </ModalBody>

      <ModalFooter style={footerStyle}>
        {!resultado ? (
          <>
            <Button
              color="secondary"
              outline
              onClick={handleClose}
              disabled={sending}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmar}
              disabled={sending}
            >
              {sending ? <Spinner size="sm" /> : "Enviar solicitação"}
            </Button>
          </>
        ) : (
          <Button color="primary" onClick={handleClose}>
            Fechar
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default SolicitarCotacaoModal;
